import React, { useMemo } from 'react';
import ActionSheet from '@medflex/components/stories/ActionSheet';
import copy from 'copy-to-clipboard';
import PropTypes from 'prop-types';
import {
  IconExclamationSolid,
  IconNavigationMore,
  IconTrash,
  IconViewShow,
  IconCopy,
  IconDownload,
} from '@medflex/components/stories/Icons';
import { IdentificationIcon } from '@heroicons/react/24/solid';
import PopupMenu from '@medflex/components/stories/PopupMenu';
import isAudioFiles from '@medflex/utils/isAudioFiles';
import isWebView from '@medflex/utils/isWebView';
import { showSuccessToast } from '@medflex/components/stories/Toast';
import { useTranslation } from 'react-i18next';
import useIsWideScreen from '@medflex/hooks/useIsWideScreen';

const SessionChatMessageContextMenu = ({
  hidden,
  children,
  message,
  downloadFile,
  onToggleImportant,
  onMarkedAsUnread,
  onDelete,
  onWriteExamination,
  isPamSession,
}) => {
  const { t } = useTranslation('global');
  const isWideScreen = useIsWideScreen();

  const handleImportantToggle = () => {
    onToggleImportant({ ...message });
  };

  const handleMarkAsUnreadClicked = () => {
    onMarkedAsUnread({ ...message });
  };

  const handleDelete = () => {
    onDelete({ ...message });
  };

  const copyText = text => {
    copy(text);
    showSuccessToast(t('ChatMessageStream.action_menu.copied'), null, {
      toastId: 'chatTextCopySuccess',
    });
  };

  const isAudioMessage = useMemo(() => {
    if (!message || !message.files) {
      return false;
    }
    return isAudioFiles(message.files);
  }, [message]);

  if (isWideScreen && !hidden) {
    return (
      <PopupMenu>
        <PopupMenu.Menu>
          <PopupMenu.Group>
            <PopupMenu.Item onClick={handleMarkAsUnreadClicked}>
              <IconViewShow size="5" className="mr-3" />
              {t('ChatMessageStream.action_menu.unread')}
            </PopupMenu.Item>
            {!isPamSession && (
              <PopupMenu.Item onClick={handleImportantToggle}>
                <IconExclamationSolid size="5" className="mr-3" />
                {message.important
                  ? t('ChatMessageStream.action_menu.not_important')
                  : t('ChatMessageStream.action_menu.important')}
              </PopupMenu.Item>
            )}
            {message.isSender && message.type !== 'welcome' && (
              <PopupMenu.Item onClick={handleDelete}>
                <IconTrash size="5" className="mr-3" />
                {t('ChatMessageStream.action_menu.delete')}
              </PopupMenu.Item>
            )}
            {message.text && (
              <PopupMenu.Item onClick={() => copyText(message.text)}>
                <IconCopy size="5" className="mr-3" />
                {t('ChatMessageStream.action_menu.copy')}
              </PopupMenu.Item>
            )}
            {isAudioMessage && !isWebView() && (
              <PopupMenu.Item onClick={event => downloadFile(event, message.files[0])}>
                <IconDownload size="5" className="mr-3" />
                {t('ChatMessageStream.action_menu.download')}
              </PopupMenu.Item>
            )}
            {onWriteExamination && (
              <PopupMenu.Item onClick={() => onWriteExamination(message)}>
                <IdentificationIcon className="w-5 h-5" />
                <span className="ml-3">{t('ChatMessageStream.action_menu.write_examination')}</span>
              </PopupMenu.Item>
            )}
          </PopupMenu.Group>
        </PopupMenu.Menu>
        <PopupMenu.Trigger>
          {({ triggerRef }) => (
            <button
              type="button"
              onClick={e => {
                e.preventDefault();
              }}
              ref={triggerRef}
              className="hover:bg-slate-300 m-1 p-1 focus:outline-none rounded-lg text-slate-600 w-8 h-8 flex justify-center items-center flex-shrink-0 cursor-pointer"
            >
              <IconNavigationMore />
            </button>
          )}
        </PopupMenu.Trigger>
      </PopupMenu>
    );
  }
  if (!isWideScreen) {
    return (
      <ActionSheet initialVisible={false}>
        <ActionSheet.Menu closeButtonLabel={t('actions.close_button')}>
          <ActionSheet.Item onClick={handleMarkAsUnreadClicked}>
            <IconViewShow size="5" className="mr-3" />
            {t('ChatMessageStream.action_menu.unread')}
          </ActionSheet.Item>
          {!isPamSession && (
            <ActionSheet.Item onClick={handleImportantToggle}>
              <IconExclamationSolid size="5" className="mr-3" />
              {message.important
                ? t('ChatMessageStream.action_menu.not_important')
                : t('ChatMessageStream.action_menu.important')}
            </ActionSheet.Item>
          )}

          {message.isSender && message.type !== 'welcome' && (
            <ActionSheet.Item onClick={handleDelete}>
              <IconTrash size="5" className="mr-3" />
              {t('ChatMessageStream.action_menu.delete')}
            </ActionSheet.Item>
          )}
          {message.text && (
            <ActionSheet.Item onClick={() => copyText(message.text)}>
              <IconCopy size="5" className="mr-3" />
              {t('ChatMessageStream.action_menu.copy')}
            </ActionSheet.Item>
          )}
          {isAudioMessage && !isWebView() && (
            <ActionSheet.Item onClick={event => downloadFile(event, message.files[0])}>
              <IconDownload size="5" className="mr-3" />
              {t('ChatMessageStream.action_menu.download')}
            </ActionSheet.Item>
          )}
          {onWriteExamination && (
            <ActionSheet.Item onClick={() => onWriteExamination(message)}>
              <IdentificationIcon className="w-5 h-5" />
              <span className="ml-3">{t('ChatMessageStream.action_menu.write_examination')}</span>
            </ActionSheet.Item>
          )}
        </ActionSheet.Menu>
        <ActionSheet.Trigger>
          {({ setIsVisible, isVisible }) => {
            const toggle = () => {
              setIsVisible(!isVisible);
            };
            children({ toggle });
          }}
        </ActionSheet.Trigger>
      </ActionSheet>
    );
  }
  return null;
};

SessionChatMessageContextMenu.propTypes = {
  hidden: PropTypes.bool,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  message: PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.string,
    files: PropTypes.array,
    important: PropTypes.bool,
    isSender: PropTypes.bool,
    text: PropTypes.string,
    time: PropTypes.string,
    user: PropTypes.shape({
      fullNameWithTitle: PropTypes.string,
    }),
  }),
  downloadFile: PropTypes.func,
  onToggleImportant: PropTypes.func,
  onMarkedAsUnread: PropTypes.func,
  onDelete: PropTypes.func,
  isPamSession: PropTypes.bool,
  onWriteExamination: PropTypes.func,
};

SessionChatMessageContextMenu.defaultProps = {
  hidden: false,
  children: null,
  message: null,
  isPamSession: false,
  onToggleImportant: () => {},
  onMarkedAsUnread: () => {},
  onDelete: () => {},
  downloadFile: () => {},
  onWriteExamination: null,
};

export default SessionChatMessageContextMenu;
